import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Trash2, Upload, Link as LinkIcon, Pencil, Check, X, Image as ImageIcon, Sparkles, Share2, Eye, EyeOff, Loader2 } from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "../../lib/api";
import useBulkSelect from "../../lib/useBulkSelect";
import BulkActionBar, { BulkCheckbox } from "../../components/BulkActionBar";
import { brand } from "../../context/CompanyContext";

function resolveImg(item) {
  if (!item.image_url) return "";
  if (item.image_url.startsWith("http")) return item.image_url;
  return BACKEND_URL + item.image_url;
}

/** Shareable caption — brand text appended so social posts always link back. */
function buildShareText(p) {
  const title = p.title && p.title !== "Foto" ? p.title : `${brand()}`;
  const caption = p.caption ? `\n${p.caption}` : "";
  return `${title}${caption}\n\nGestão profissional de limpezas · Madeira\nluxstaymadeira.pt`;
}

async function sharePhoto(p) {
  const url = resolveImg(p);
  const shareText = buildShareText(p);
  const shareData = { title: p.title || `${brand()}`, text: shareText, url };
  // Try to include the file itself for richer previews on mobile
  try {
    const resp = await fetch(url);
    const blob = await resp.blob();
    const file = new File([blob], "luxstay.jpg", { type: blob.type || "image/jpeg" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ ...shareData, files: [file] });
      return "shared-file";
    }
  } catch (e) {
    // fall through
  }
  if (navigator.share) {
    try {
      await navigator.share(shareData);
      return "shared";
    } catch (e) {
      if (e.name === "AbortError") return "cancelled";
    }
  }
  return "fallback";
}

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("file"); // 'file' | 'url'
  const [files, setFiles] = useState([]); // multiple files
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [cat, setCat] = useState("auto");
  const [uploading, setUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
  const [editing, setEditing] = useState(null); // {id, title, caption}
  const [aiBusy, setAiBusy] = useState(null); // photo id while regenerating
  const [rotateBusy, setRotateBusy] = useState(null); // photo id while rotating
  const [shareMenu, setShareMenu] = useState(null); // photo id for open share menu

  async function regenerateCaption(pid) {
    setAiBusy(pid);
    try {
      await api.post(`/admin/gallery/${pid}/ai-caption`);
      toast.success("Legenda gerada pela IA");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setAiBusy(null);
  }

  async function rotatePhoto(pid) {
    setRotateBusy(pid);
    try {
      await api.post(`/admin/gallery/${pid}/rotate?degrees=90`);
      toast.success("Foto rodada");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setRotateBusy(null);
  }

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/gallery");
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  const ids = useMemo(() => items.map(p => p.id), [items]);
  const bulk = useBulkSelect(ids);
  async function bulkRun(selected, fn, { successMsg }) {
    const res = await Promise.allSettled(selected.map(fn));
    const ok = res.filter(r => r.status === "fulfilled").length;
    const ko = res.length - ok;
    if (ok > 0) toast.success(`${ok} ${successMsg}${ko > 0 ? ` · ${ko} falharam` : ""}`);
    else toast.error("Nenhuma operação concluída");
    load();
  }

  async function submit(e) {
    e.preventDefault();
    if (mode === "file" && files.length === 0) { toast.error("Escolha pelo menos uma foto"); return; }
    if (mode === "url" && !url.trim()) { toast.error("Cole um URL de imagem"); return; }
    setUploading(true);
    try {
      if (mode === "file" && files.length > 1) {
        // Multi-photo path — one HTTP request PER photo so we never hit the
        // proxy's request-size limit (otherwise 53 photos × ~5MB = 413).
        // Concurrency 3 keeps the UI responsive while still moving fast.
        setBulkProgress({ done: 0, total: files.length });
        let doneOk = 0;
        let doneErr = 0;
        const CONCURRENCY = 3;
        let cursor = 0;
        toast.info(`A enviar ${files.length} fotos · a IA vai escolher categoria e legenda para cada uma…`);
        async function worker() {
          while (true) {
            const idx = cursor++;
            if (idx >= files.length) break;
            const f = files[idx];
            const fd = new FormData();
            fd.append("file", f);
            // Send empty title so the backend asks the AI to write one.
            // iOS uploads typically have UUID filenames which look terrible.
            fd.append("title", "");
            fd.append("caption", "");
            fd.append("category", cat);
            try {
              await api.post("/admin/gallery", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 0,
              });
              doneOk += 1;
            } catch (err) {
              doneErr += 1;
              // Log but keep going — one bad photo shouldn't kill the batch.
              // eslint-disable-next-line no-console
              console.warn("upload failed:", f.name, formatApiError(err));
            }
            setBulkProgress({ done: doneOk + doneErr, total: files.length });
          }
        }
        await Promise.all(
          Array.from({ length: Math.min(CONCURRENCY, files.length) }, () => worker()),
        );
        if (doneOk > 0) {
          toast.success(`${doneOk} foto(s) adicionadas${doneErr > 0 ? ` · ${doneErr} falharam` : ""}`);
        } else {
          toast.error("Nenhuma foto pôde ser enviada.");
        }
      } else {
        // Single-file or URL — keeps the rich title/caption form.
        const fd = new FormData();
        if (mode === "file") fd.append("file", files[0]);
        else fd.append("url", url.trim());
        fd.append("title", title);
        fd.append("caption", caption);
        fd.append("category", cat);
        await api.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Foto adicionada");
      }
      setFiles([]); setUrl(""); setTitle(""); setCaption("");
      setBulkProgress({ done: 0, total: 0 });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setUploading(false);
  }

  async function remove(id) {
    if (!window.confirm("Eliminar esta foto da galeria?")) return;
    try {
      await api.delete(`/admin/gallery/${id}`);
      toast.success("Foto removida");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  async function saveEdit() {
    try {
      await api.patch(`/admin/gallery/${editing.id}`, {
        title: editing.title, caption: editing.caption,
      });
      toast.success("Atualizado");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  async function reorder(id, dir) {
    const idx = items.findIndex((x) => x.id === id);
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const a = items[idx], b = items[target];
    try {
      await Promise.all([
        api.patch(`/admin/gallery/${a.id}`, { order: b.order }),
        api.patch(`/admin/gallery/${b.id}`, { order: a.order }),
      ]);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  const [fixingTitles, setFixingTitles] = useState(false);
  const [optimising, setOptimising] = useState(false);
  async function fixGarbageTitles() {
    if (!window.confirm("A IA vai analisar todas as fotos com títulos tipo UUID/IMG_xxxx e dar-lhes um título humano. Pode demorar 1-3 minutos. Continuar?")) return;
    setFixingTitles(true);
    try {
      const { data } = await api.post("/admin/gallery/fix-titles", null, { timeout: 0 });
      toast.success(`Títulos corrigidos: ${data.fixed} · saltadas: ${data.skipped} · falhas: ${data.failed}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setFixingTitles(false);
  }

  async function optimizePhotos() {
    if (!window.confirm("Vai redimensionar e recomprimir todas as fotos já carregadas para que carreguem 5-10× mais rápido. Pode demorar 1-2 minutos. Continuar?")) return;
    setOptimising(true);
    try {
      const { data } = await api.post("/admin/gallery/optimize", null, { timeout: 0 });
      toast.success(`Otimizadas ${data.optimised} fotos · poupados ${data.human_saved} · saltadas ${data.skipped}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setOptimising(false);
  }

  return (
    <div className="space-y-8" data-testid="admin-gallery-page">
      <header>
        <div className="eyebrow text-[#d4af37]">Admin · Galeria</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">Fotos públicas da galeria</h1>
        <p className="text-sm text-[#a0b2af] mt-3 max-w-2xl leading-relaxed">
          Estas fotos aparecem em <code className="text-[#d4af37]">/galeria</code> para que
          qualquer pessoa possa ver o trabalho que a sua equipa entrega. Carregue fotos reais
          para substituir as imagens iniciais e ganhar a confiança de novos clientes.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" onClick={fixGarbageTitles} disabled={fixingTitles}
                  className="px-3 py-2 text-xs uppercase tracking-widest border border-[#d4af37] text-[#d4af37] bg-[#d4af37]/10 disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="gallery-fix-titles-btn">
            <Sparkles size={14} />
            {fixingTitles ? "A corrigir títulos…" : "Corrigir títulos antigos com IA"}
          </button>
          <button type="button" onClick={optimizePhotos} disabled={optimising}
                  className="px-3 py-2 text-xs uppercase tracking-widest border border-[#3b82f6] text-[#3b82f6] bg-[#3b82f6]/10 disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="gallery-optimize-btn">
            <ImageIcon size={14} />
            {optimising ? "A otimizar…" : "Otimizar fotos antigas (mais leves)"}
          </button>
        </div>
      </header>

      {/* GLOBAL VISIBILITY — controls whether ANY non-admin user can see
          the public /galeria page or the navigation links to it. */}
      <GalleryGlobalToggle />

      <section className="lux-card p-6 space-y-4" data-testid="admin-gallery-form">
        <div className="flex gap-2">
          <button type="button" onClick={() => setMode("file")}
                  className={`px-3 py-2 text-xs uppercase tracking-widest border transition-colors inline-flex items-center gap-2 ${mode === "file" ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/10" : "border-[#1a4640] text-[#a0b2af]"}`}
                  data-testid="gallery-mode-file">
            <Upload size={14} /> Ficheiro
          </button>
          <button type="button" onClick={() => setMode("url")}
                  className={`px-3 py-2 text-xs uppercase tracking-widest border transition-colors inline-flex items-center gap-2 ${mode === "url" ? "border-[#d4af37] text-[#d4af37] bg-[#d4af37]/10" : "border-[#1a4640] text-[#a0b2af]"}`}
                  data-testid="gallery-mode-url">
            <LinkIcon size={14} /> URL externo
          </button>
        </div>

        <form onSubmit={submit} className="grid gap-3 md:grid-cols-2">
          {mode === "file" ? (
            <label className="block md:col-span-2">
              <span className="text-xs uppercase tracking-widest text-[#a0b2af]">
                Fotos (JPG / PNG / WEBP) — pode escolher várias sem limite
              </span>
              <input type="file" accept="image/jpeg,image/png,image/webp"
                     multiple
                     onChange={(e) => setFiles(Array.from(e.target.files || []))}
                     className="mt-1 w-full text-sm text-[#fdfbf7] file:mr-3 file:px-3 file:py-2 file:border-0 file:bg-[#d4af37] file:text-[#010c0a] file:text-xs file:uppercase file:tracking-widest"
                     data-testid="gallery-file-input" />
              {files.length > 0 && (
                <div className="mt-2 text-[11px] text-[#d4af37]" data-testid="gallery-file-count">
                  {files.length === 1
                    ? "1 foto selecionada · título e legenda manuais (ou IA se vazio)"
                    : `${files.length} fotos selecionadas · a IA vai escolher categoria e escrever legenda única para cada uma`}
                </div>
              )}
            </label>
          ) : (
            <label className="block md:col-span-2">
              <span className="text-xs uppercase tracking-widest text-[#a0b2af]">URL da imagem</span>
              <input type="url" value={url} onChange={(e) => setUrl(e.target.value)}
                     placeholder="https://…"
                     className="mt-1 w-full bg-[#062c27] border border-[#1a4640] px-3 py-2 text-sm text-[#fdfbf7] focus:outline-none focus:border-[#d4af37]"
                     data-testid="gallery-url-input" />
            </label>
          )}

          {(mode === "url" || files.length <= 1) && (
            <>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-[#a0b2af]">Título</span>
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                       placeholder="Ex: Quarto impecável"
                       className="mt-1 w-full bg-[#062c27] border border-[#1a4640] px-3 py-2 text-sm text-[#fdfbf7] focus:outline-none focus:border-[#d4af37]"
                       data-testid="gallery-title-input" />
              </label>

              <label className="block">
                <span className="text-xs uppercase tracking-widest text-[#a0b2af]">Descrição curta (opcional · IA gera se deixar vazio)</span>
                <input type="text" value={caption} onChange={(e) => setCaption(e.target.value)}
                       placeholder="Deixe vazio e a IA analisa a foto e escreve por si."
                       className="mt-1 w-full bg-[#062c27] border border-[#1a4640] px-3 py-2 text-sm text-[#fdfbf7] focus:outline-none focus:border-[#d4af37]"
                       data-testid="gallery-caption-input" />
              </label>
            </>
          )}

          <label className="block md:col-span-2">
            <span className="text-xs uppercase tracking-widest text-[#a0b2af]">Categoria</span>
            <select value={cat} onChange={(e) => setCat(e.target.value)}
                    className="mt-1 w-full bg-[#062c27] border border-[#1a4640] px-3 py-2 text-sm text-[#fdfbf7] focus:outline-none focus:border-[#d4af37]"
                    data-testid="gallery-category-input">
              <option value="auto">Automática (IA escolhe por foto)</option>
              <option value="quarto">Quarto</option>
              <option value="cozinha">Cozinha</option>
              <option value="casa_de_banho">Casa de banho</option>
              <option value="sala">Sala</option>
              <option value="detalhes">Detalhes</option>
              <option value="outros">Outros</option>
            </select>
          </label>

          <div className="md:col-span-2 space-y-2">
            {uploading && bulkProgress.total > 1 && (
              <div className="space-y-1.5" data-testid="gallery-bulk-progress">
                <div className="flex items-center justify-between text-[11px] text-[#d4af37]">
                  <span>A processar com IA…</span>
                  <span>{bulkProgress.done} / {bulkProgress.total}</span>
                </div>
                <div className="h-2 bg-[#1a4640] rounded-full overflow-hidden">
                  <div className="h-full bg-[#d4af37] transition-all duration-500"
                       style={{ width: `${Math.round((bulkProgress.done / bulkProgress.total) * 100)}%` }} />
                </div>
              </div>
            )}
            <button type="submit" disabled={uploading}
                    className="btn-gold px-6 py-2.5 text-xs uppercase tracking-widest disabled:opacity-50"
                    data-testid="gallery-submit-btn">
              {uploading
                ? (bulkProgress.total > 1
                    ? `A enviar ${bulkProgress.done}/${bulkProgress.total}…`
                    : "A carregar…")
                : (files.length > 1
                    ? `Adicionar ${files.length} fotos (IA gera categoria e legenda)`
                    : "Adicionar à galeria")}
            </button>
          </div>
        </form>
      </section>

      <section>
        <div className="flex items-center justify-between mb-4">
          <div className="eyebrow text-[#a0b2af] flex items-center gap-2">
            <ImageIcon size={14} /> {items.length} {items.length === 1 ? "foto" : "fotos"} publicadas
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-[#a0b2af]">A carregar…</div>
        ) : items.length === 0 ? (
          <div className="lux-card p-8 text-center text-sm text-[#a0b2af]">
            Ainda não há fotos. Adicione a primeira acima.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.length > 0 && (
              <label className="col-span-full inline-flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-widest text-[#a0b2af] border border-[#1a4640] rounded-sm w-fit cursor-pointer hover:text-[#d4af37] hover:border-[#d4af37]/50">
                <BulkCheckbox
                  checked={bulk.allSelected}
                  indeterminate={bulk.someSelected}
                  onChange={() => bulk.toggleAll(ids)}
                  label="Selecionar todas"
                />
                Selecionar todas
              </label>
            )}
            {items.map((p, i) => (
              <div key={p.id}
                   className={`lux-card overflow-hidden group transition-colors ${bulk.isSelected(p.id) ? "ring-2 ring-[#d4af37]" : ""}`}
                   data-testid={`gallery-item-${p.id}`}>
                <div className="relative aspect-[4/3] bg-[#062c27]">
                  {p.image_url && (
                    <img src={resolveImg(p)} alt={p.title} loading="lazy"
                         className="w-full h-full object-cover" />
                  )}
                  <div className="absolute top-2 right-2 z-10 bg-[#010c0a]/70 backdrop-blur-sm p-1.5 rounded-sm">
                    <BulkCheckbox
                      id={p.id}
                      checked={bulk.isSelected(p.id)}
                      onChange={() => bulk.toggle(p.id)}
                      label="Selecionar foto"
                    />
                  </div>
                  {p.is_default && (
                    <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] uppercase tracking-widest bg-[#010c0a]/70 border border-[#d4af37]/40 text-[#d4af37]">
                      Inicial
                    </span>
                  )}
                </div>
                <div className="p-4 space-y-3">
                  {editing?.id === p.id ? (
                    <>
                      <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                             className="w-full bg-[#062c27] border border-[#1a4640] px-2 py-1.5 text-sm text-[#fdfbf7] focus:outline-none focus:border-[#d4af37]"
                             data-testid={`gallery-edit-title-${p.id}`} />
                      <textarea value={editing.caption} onChange={(e) => setEditing({ ...editing, caption: e.target.value })}
                                rows={3}
                                className="w-full bg-[#062c27] border border-[#1a4640] px-2 py-1.5 text-sm text-[#fdfbf7] focus:outline-none focus:border-[#d4af37]"
                                data-testid={`gallery-edit-caption-${p.id}`} />
                      <div className="flex gap-2">
                        <button onClick={saveEdit}
                                className="flex-1 px-3 py-1.5 text-xs uppercase tracking-widest border border-[#10b981]/50 text-[#10b981] hover:bg-[#10b981]/10 inline-flex items-center justify-center gap-1"
                                data-testid={`gallery-save-${p.id}`}>
                          <Check size={14} /> Guardar
                        </button>
                        <button onClick={() => setEditing(null)}
                                className="px-3 py-1.5 text-xs uppercase tracking-widest border border-[#1a4640] text-[#a0b2af] hover:text-[#fdfbf7] inline-flex items-center gap-1">
                          <X size={14} /> Cancelar
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="font-display text-base text-[#fdfbf7]">{p.title}</div>
                      <p className="text-xs text-[#a0b2af] leading-relaxed min-h-[32px]">{p.caption}</p>
                      <div className="flex items-center gap-2 pt-2">
                        <button onClick={() => reorder(p.id, -1)} disabled={i === 0}
                                className="px-2 py-1 text-xs border border-[#1a4640] text-[#a0b2af] hover:text-[#d4af37] disabled:opacity-30"
                                data-testid={`gallery-up-${p.id}`}>↑</button>
                        <button onClick={() => reorder(p.id, 1)} disabled={i === items.length - 1}
                                className="px-2 py-1 text-xs border border-[#1a4640] text-[#a0b2af] hover:text-[#d4af37] disabled:opacity-30"
                                data-testid={`gallery-down-${p.id}`}>↓</button>
                        <button onClick={() => setEditing({ id: p.id, title: p.title, caption: p.caption })}
                                className="ml-auto px-2 py-1 text-xs uppercase tracking-widest border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 inline-flex items-center gap-1"
                                data-testid={`gallery-edit-${p.id}`}>
                          <Pencil size={12} /> Editar
                        </button>
                        <button onClick={() => regenerateCaption(p.id)} disabled={aiBusy === p.id}
                                className="px-2 py-1 text-xs uppercase tracking-widest border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10 inline-flex items-center gap-1 disabled:opacity-60"
                                data-testid={`gallery-ai-caption-${p.id}`}
                                title="Gerar legenda com IA a partir do título">
                          <Sparkles size={12} /> {aiBusy === p.id ? "…" : "IA"}
                        </button>
                        <button onClick={() => rotatePhoto(p.id)} disabled={rotateBusy === p.id || !p.storage_path}
                                className="px-2 py-1 text-xs uppercase tracking-widest border border-[#3b82f6]/40 text-[#3b82f6] hover:bg-[#3b82f6]/10 inline-flex items-center gap-1 disabled:opacity-30"
                                data-testid={`gallery-rotate-${p.id}`}
                                title={p.storage_path ? "Rodar 90° para a direita" : "Fotos externas (URL) não podem ser rodadas"}>
                          {rotateBusy === p.id ? <Loader2 size={12} className="animate-spin" /> : "↻"} 90°
                        </button>
                        <ShareDropdown photo={p}
                                       open={shareMenu === p.id}
                                       onToggle={() => setShareMenu(shareMenu === p.id ? null : p.id)}
                                       onClose={() => setShareMenu(null)} />
                        <button onClick={() => remove(p.id)}
                                className="px-2 py-1 text-xs uppercase tracking-widest border border-[#ef4444]/60 text-[#fca5a5] hover:bg-[#ef4444]/10 inline-flex items-center gap-1"
                                data-testid={`gallery-delete-${p.id}`}>
                          <Trash2 size={12} /> Eliminar
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <BulkActionBar
        count={bulk.count}
        ids={Array.from(bulk.selected)}
        onClear={bulk.clear}
        actions={[
          {
            key: "ai", label: "Gerar IA (título + legenda + categoria)",
            icon: Sparkles,
            onClick: (sel) => bulkRun(
              sel,
              (id) => api.post(`/admin/gallery/${id}/ai-caption`, null, { timeout: 0 }),
              { successMsg: "foto(s) atualizada(s) pela IA" },
            ),
          },
          {
            key: "rotate", label: "Rodar 90°", icon: ImageIcon,
            onClick: (sel) => bulkRun(
              sel,
              (id) => api.post(`/admin/gallery/${id}/rotate?degrees=90`),
              { successMsg: "foto(s) rodada(s)" },
            ),
          },
          {
            key: "delete", label: "Eliminar", icon: Trash2, variant: "danger",
            confirm: "Eliminar {count} foto(s) da galeria? Ação irreversível.",
            onClick: (sel) => bulkRun(sel, (id) => api.delete(`/admin/gallery/${id}`), { successMsg: "foto(s) eliminada(s)" }),
          },
        ]}
      />
    </div>
  );
}

/**
 * ShareDropdown — one-click share of a gallery photo to major social
 * networks. Uses the Web Share API when available (mobile devices get the
 * native share sheet with the image attached); desktop falls back to
 * deep-links that pre-populate captions for each network.
 */
function ShareDropdown({ photo, open, onToggle, onClose }) {
  const img = resolveImg(photo);
  const defaultText = buildShareText(photo);

  async function handleNative(e) {
    e.stopPropagation();
    onClose();
    const r = await sharePhoto(photo);
    if (r === "fallback") {
      // Re-open menu so user can pick a specific network
      onToggle();
    } else if (r === "shared" || r === "shared-file") {
      toast.success("Partilhado");
    }
  }

  function openIn(urlBuilder) {
    const u = urlBuilder({ img, text: defaultText, caption: photo.caption || photo.title });
    window.open(u, "_blank", "noopener,noreferrer,width=720,height=640");
    onClose();
  }

  function copyLink(e) {
    e.stopPropagation();
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(img).then(
        () => { toast.success("Link copiado"); onClose(); },
        () => toast.error("Não foi possível copiar"),
      );
    } else {
      toast.error("Navegador não suporta copiar");
    }
  }

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button onClick={(e) => { e.stopPropagation(); onToggle(); }}
              className="px-2 py-1 text-xs uppercase tracking-widest border border-[#3b82f6]/50 text-[#93c5fd] hover:bg-[#3b82f6]/10 inline-flex items-center gap-1"
              data-testid={`gallery-share-${photo.id}`}
              title="Partilhar nas redes sociais">
        <Share2 size={12} /> Partilhar
      </button>

      {open && (
        <>
          <button aria-hidden className="fixed inset-0 z-40 cursor-default"
                  onClick={onClose} tabIndex={-1} />
          <div className="absolute right-0 top-full mt-1 w-56 bg-[#031c18] border border-[#1a4640] rounded-sm shadow-xl z-50 overflow-hidden"
               data-testid={`gallery-share-menu-${photo.id}`}>
            <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-[#a0b2af] border-b border-[#1a4640]">
              Partilhar em
            </div>
            <button onClick={handleNative}
                    className="w-full px-3 py-2.5 text-left text-xs text-[#fdfbf7] hover:bg-[#062c27] flex items-center gap-2"
                    data-testid={`share-native-${photo.id}`}>
              <Share2 size={12} className="text-[#d4af37]" /> Sistema (nativo)
            </button>
            <button onClick={() => openIn(({ text: t, img: i }) =>
                    `https://wa.me/?text=${encodeURIComponent(t + "\n\n" + i)}`)}
                    className="w-full px-3 py-2.5 text-left text-xs text-[#fdfbf7] hover:bg-[#062c27] flex items-center gap-2"
                    data-testid={`share-whatsapp-${photo.id}`}>
              <span className="w-3 h-3 rounded-full bg-[#25D366]" /> WhatsApp
            </button>
            <button onClick={() => openIn(({ img: i, caption: c }) =>
                    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(i)}&quote=${encodeURIComponent(c || "")}`)}
                    className="w-full px-3 py-2.5 text-left text-xs text-[#fdfbf7] hover:bg-[#062c27] flex items-center gap-2"
                    data-testid={`share-facebook-${photo.id}`}>
              <span className="w-3 h-3 rounded-full bg-[#1877F2]" /> Facebook
            </button>
            <button onClick={() => openIn(({ text: t, img: i }) =>
                    `https://twitter.com/intent/tweet?text=${encodeURIComponent(t)}&url=${encodeURIComponent(i)}`)}
                    className="w-full px-3 py-2.5 text-left text-xs text-[#fdfbf7] hover:bg-[#062c27] flex items-center gap-2"
                    data-testid={`share-twitter-${photo.id}`}>
              <span className="w-3 h-3 rounded-full bg-black border border-white/20" /> X / Twitter
            </button>
            <button onClick={() => openIn(({ text: t, img: i }) =>
                    `https://t.me/share/url?url=${encodeURIComponent(i)}&text=${encodeURIComponent(t)}`)}
                    className="w-full px-3 py-2.5 text-left text-xs text-[#fdfbf7] hover:bg-[#062c27] flex items-center gap-2"
                    data-testid={`share-telegram-${photo.id}`}>
              <span className="w-3 h-3 rounded-full bg-[#26A5E4]" /> Telegram
            </button>
            <button onClick={() => openIn(({ img: i, caption: c }) =>
                    `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(i)}&summary=${encodeURIComponent(c || "")}`)}
                    className="w-full px-3 py-2.5 text-left text-xs text-[#fdfbf7] hover:bg-[#062c27] flex items-center gap-2"
                    data-testid={`share-linkedin-${photo.id}`}>
              <span className="w-3 h-3 rounded-full bg-[#0A66C2]" /> LinkedIn
            </button>
            <div className="border-t border-[#1a4640]">
              <button onClick={copyLink}
                      className="w-full px-3 py-2.5 text-left text-xs text-[#a0b2af] hover:bg-[#062c27] flex items-center gap-2"
                      data-testid={`share-copy-${photo.id}`}>
                <LinkIcon size={12} /> Copiar link
              </button>
              <a href={img} download={`luxstay-${photo.id}.jpg`}
                 onClick={onClose}
                 className="w-full px-3 py-2.5 text-left text-xs text-[#a0b2af] hover:bg-[#062c27] flex items-center gap-2"
                 data-testid={`share-download-${photo.id}`}>
                <Upload size={12} className="rotate-180" /> Transferir imagem
              </a>
              <div className="px-3 py-2 text-[10px] text-[#a0b2af] bg-[#041f1b] border-t border-[#1a4640]">
                <strong className="text-[#d4af37]">Instagram:</strong> transfira a imagem e publique a partir da aplicação — a API pública não permite upload direto.
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


/**
 * Big, prominent visibility toggle right at the top of /admin/galeria.
 * The same flag is also editable from /admin/promocao but the admin asked
 * for it directly on the photos page so they can flip it from where the
 * photos live. Persists in db.settings({key: "gallery_visible"}).
 *
 * - When enabled: public /galeria works, non-admin nav menus show the link.
 * - When disabled: only the admin keeps seeing photos. Public visitors,
 *   clients and employees stop seeing the link/page entirely.
 */
function GalleryGlobalToggle() {
  const [enabled, setEnabled] = useState(null);   // null = loading
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/gallery/status")
      .then(({ data }) => setEnabled(!!data.enabled))
      .catch(() => setEnabled(true));
  }, []);

  async function toggle() {
    if (enabled === null) return;
    const next = !enabled;
    setBusy(true);
    try {
      await api.post("/admin/gallery/visibility", { enabled: next });
      setEnabled(next);
      toast.success(next
        ? "Galeria ativada — agora visível a clientes e visitantes"
        : "Galeria desativada — apenas o admin pode ver");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  }

  if (enabled === null) {
    return (
      <div className="lux-card p-5 text-sm text-[#a0b2af]"
           data-testid="gallery-global-toggle-loading">
        A verificar visibilidade…
      </div>
    );
  }

  return (
    <div className={`lux-card p-5 md:p-6 border-2 transition-colors ${
           enabled ? "border-[#10b981]/50 bg-[#10b981]/5"
                    : "border-[#a0b2af]/30 bg-[#031411]"
         }`}
         data-testid="gallery-global-toggle">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full grid place-items-center shrink-0 ${
            enabled ? "bg-[#10b981]/15 text-[#10b981]" : "bg-[#a0b2af]/10 text-[#a0b2af]"
          }`}>
            {enabled ? <Eye size={18} /> : <EyeOff size={18} />}
          </div>
          <div>
            <div className="font-display text-xl text-[#fdfbf7]">
              {enabled ? "Galeria visível ao público" : "Galeria oculta"}
            </div>
            <div className="text-xs text-[#a0b2af] mt-1 max-w-md">
              {enabled
                ? "Toda a gente (visitantes, clientes, funcionários) vê esta galeria. Para ocultar, clique no botão à direita."
                : "Apenas o admin vê estas fotos. Visitantes, clientes e funcionários não veem nem a opção «Galeria» nos menus."}
            </div>
          </div>
        </div>
        <button onClick={toggle} disabled={busy}
                className={`px-5 py-2.5 text-xs uppercase tracking-widest border rounded-sm inline-flex items-center gap-2 transition-colors disabled:opacity-50 ${
                  enabled
                    ? "border-[#a0b2af]/40 text-[#a0b2af] hover:border-[#ef4444] hover:text-[#ef4444]"
                    : "border-[#10b981]/60 text-[#10b981] hover:bg-[#10b981]/10"
                }`}
                data-testid="gallery-global-toggle-btn">
          {busy
            ? <Loader2 size={12} className="animate-spin" />
            : (enabled ? <EyeOff size={12} /> : <Eye size={12} />)}
          {enabled ? "Desativar galeria" : "Ativar galeria"}
        </button>
      </div>
    </div>
  );
}
